/**
 * ESG platform color tokens — single source of truth for MUI theme, CSS, and charts.
 * Recyclee blue brand with slate neutrals tuned for high contrast (WCAG AA+).
 * No greens anywhere: positive / success states use blue as well.
 */

export const APP_BRAND = {
  primary: {
    main: '#0000FE',
    light: '#4D4DFF',
    dark: '#0000CC',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#1E4FB8',
    light: '#3568D4',
    dark: '#163A8A',
    contrastText: '#FFFFFF',
  },
} as const;

export const APP_SEMANTIC = {
  success: {
    main: '#1E4FB8',
    light: '#3568D4',
    dark: '#163A8A',
    contrastText: '#FFFFFF',
  },
  error: {
    main: '#C62828',
    light: '#D64545',
    dark: '#9B1C1C',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#A66A12',
    light: '#D4922A',
    dark: '#7A4E0C',
    contrastText: '#FFFFFF',
  },
  info: {
    main: '#1E4FB8',
    light: '#3568D4',
    dark: '#163A8A',
    contrastText: '#FFFFFF',
  },
} as const;

/** Accessible text — wired into MUI `palette.text`. */
export const APP_TEXT_COLOR = {
  light: {
    primary: '#0F172A',
    secondary: '#334155',
    disabled: '#64748B',
  },
  dark: {
    primary: '#F8FAFC',
    secondary: '#CBD5E1',
    disabled: '#94A3B8',
  },
} as const;

export const APP_SURFACES = {
  light: {
    default: '#E8EDF3',
    paper: '#FFFFFF',
    elevated: '#F1F5F9',
    input: '#F1F5F9',
    inputFocused: '#FFFFFF',
    shell: '#FFFFFF',
  },
  dark: {
    default: '#0B1017',
    paper: '#151C26',
    elevated: '#1E2733',
    input: '#243041',
    inputFocused: '#2A3748',
    shell: '#151C26',
  },
} as const;

export const APP_BORDERS = {
  light: {
    subtle: 'rgba(15, 23, 42, 0.10)',
    default: 'rgba(15, 23, 42, 0.16)',
    strong: 'rgba(15, 23, 42, 0.28)',
  },
  dark: {
    subtle: 'rgba(255, 255, 255, 0.10)',
    default: 'rgba(255, 255, 255, 0.16)',
    strong: 'rgba(255, 255, 255, 0.28)',
  },
} as const;

export const APP_ACTION = {
  light: {
    hover: 'rgba(15, 23, 42, 0.06)',
    selected: 'rgba(0, 0, 254, 0.14)',
    focusRing: 'rgba(0, 0, 254, 0.22)',
  },
  dark: {
    hover: 'rgba(255, 255, 255, 0.08)',
    selected: 'rgba(77, 77, 255, 0.22)',
    focusRing: 'rgba(77, 77, 255, 0.32)',
  },
} as const;

/** Opaque, mutually distinct hues for multi-segment charts (no alpha wash). */
export const APP_CHART_COLORS = [
  '#0000FE',
  '#3D5A80',
  '#A66A12',
  '#C62828',
  '#6B21A8',
  '#1E4FB8',
  '#7A869A',
  '#9A3412',
] as const;

export const APP_SHAPE = {
  radiusSm: 10,
  radiusMd: 12,
  radiusLg: 16,
} as const;

export type AppColorMode = 'light' | 'dark';

export const APP_CSS_VARS = {
  primary: APP_BRAND.primary.main,
  primaryRgb: '0, 0, 254',
} as const;

export function getAppSurfaces(mode: AppColorMode) {
  return APP_SURFACES[mode];
}

export function getAppBorders(mode: AppColorMode) {
  return APP_BORDERS[mode];
}

export function getAppAction(mode: AppColorMode) {
  return APP_ACTION[mode];
}

/* -------------------------------------------------------------------------- */
/* ESG content-area tokens                                                     */
/*                                                                            */
/* The page content (everything inside the shell tile) uses the RECYCLEE blue  */
/* as its only accent on a cool slate canvas (#F1F5F9) — no green anywhere.    */
/* Positive / complete states use blue too; red is reserved for negatives.     */
/* Consumed by `theme/createContentTheme.ts` and `theme/chartTheme.ts`.        */
/* -------------------------------------------------------------------------- */

export const ESG_CONTENT_PALETTE = {
  light: {
    primary: { main: '#0000FE', light: '#4D4DFF', dark: '#0000CC', contrastText: '#FFFFFF' },
    secondary: { main: '#334155', light: '#475569', dark: '#0F172A', contrastText: '#FFFFFF' },
    success: { main: '#1E4FB8', light: '#3568D4', dark: '#163A8A', contrastText: '#FFFFFF' },
    warning: { main: '#A66A12', light: '#D4922A', dark: '#7A4E0C', contrastText: '#FFFFFF' },
    error: { main: '#C62828', light: '#D64545', dark: '#9B1C1C', contrastText: '#FFFFFF' },
    info: { main: '#1E4FB8', light: '#3568D4', dark: '#163A8A', contrastText: '#FFFFFF' },
    text: { primary: '#0F172A', secondary: '#475569', disabled: '#64748B' },
    background: {
      default: '#F1F5F9',
      paper: '#FFFFFF',
      elevated: '#F1F5F9',
      input: '#FFFFFF',
      inputFocused: '#FFFFFF',
      shell: '#FFFFFF',
    },
  },
  dark: {
    primary: { main: '#8080FF', light: '#A6A6FF', dark: '#4D4DFF', contrastText: '#0B1017' },
    secondary: { main: '#CBD5E1', light: '#E2E8F0', dark: '#94A3B8', contrastText: '#0B1017' },
    success: { main: '#8FB0F0', light: '#B4CBF6', dark: '#5C86D9', contrastText: '#0B1017' },
    warning: { main: '#E0A04A', light: '#EBBC7A', dark: '#B37A2C', contrastText: '#0B1017' },
    error: { main: '#F07A7A', light: '#F5A3A3', dark: '#C95252', contrastText: '#0B1017' },
    info: { main: '#8FB0F0', light: '#B4CBF6', dark: '#5C86D9', contrastText: '#0B1017' },
    text: { primary: '#F8FAFC', secondary: '#CBD5E1', disabled: '#94A3B8' },
    background: {
      default: '#0B1017',
      paper: '#151C26',
      elevated: '#1E2733',
      input: '#1E2733',
      inputFocused: '#243041',
      shell: '#151C26',
    },
  },
} as const;

export const ESG_CONTENT_BORDERS = {
  light: {
    subtle: 'rgba(15, 23, 42, 0.08)',
    default: 'rgba(15, 23, 42, 0.14)',
    strong: 'rgba(15, 23, 42, 0.28)',
    rule: '#0F172A',
  },
  dark: {
    subtle: 'rgba(255, 255, 255, 0.08)',
    default: 'rgba(255, 255, 255, 0.14)',
    strong: 'rgba(255, 255, 255, 0.26)',
    rule: '#F8FAFC',
  },
} as const;

export const ESG_CONTENT_ACTION = {
  light: {
    hover: 'rgba(15, 23, 42, 0.04)',
    selected: 'rgba(0, 0, 254, 0.08)',
    focusRing: 'rgba(0, 0, 254, 0.22)',
  },
  dark: {
    hover: 'rgba(255, 255, 255, 0.06)',
    selected: 'rgba(128, 128, 255, 0.18)',
    focusRing: 'rgba(128, 128, 255, 0.32)',
  },
} as const;

/**
 * Muted, mutually distinct series colours with no greens (blue, ochre, slate,
 * rust, plum, clay, steel, charcoal). Order matters: the first series is blue.
 */
export const ESG_CONTENT_CHART_COLORS = {
  light: ['#0000FE', '#B07A1E', '#3D5A80', '#A8442A', '#5B4B8A', '#8C6D5A', '#7A869A', '#334155'],
  dark: ['#8080FF', '#E0A04A', '#8FB0D6', '#F07A7A', '#A898D6', '#C4A48E', '#A3AEC2', '#CBD5E1'],
} as const;

/** Architectural radius scale (px) for the content area. */
export const ESG_CONTENT_SHAPE = {
  /** chips, tooltips, skeletons, bars */
  xs: 4,
  /** buttons, inputs, alerts, icon squares */
  sm: 6,
  /** panels, cards, dialogs */
  md: 8,
} as const;
